import { SvgIcon } from '@progress/kendo-react-common';
import { GridCellProps } from '@progress/kendo-react-grid';
import { DragEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GridCellBase from 'src/components/GridCellBase';
import { useContextSelector } from 'use-context-selector';
import { ShoppingListProductsContext } from '../ShoppingListProductsContext';
import { SHOPPING_LIST_REORDER_MIME, shoppingListDragIndicatorIcon } from '../shoppingListReorder';

const ShoppingListReorderHandleCell = (props: GridCellProps) => {
  const { t } = useTranslation();
  const productKey = String(props.dataItem?.productKey ?? '');
  const onReorderProducts = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onReorderProducts
  );

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLSpanElement>) => {
      if (!productKey) {
        event.preventDefault();
        return;
      }

      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(SHOPPING_LIST_REORDER_MIME, productKey);
      event.dataTransfer.setData('text/plain', productKey);

      const row = event.currentTarget.closest('tr');
      row?.classList.add('opacity-50');
    },
    [productKey]
  );

  const handleDragEnd = useCallback((event: DragEvent<HTMLSpanElement>) => {
    const row = event.currentTarget.closest('tr');
    row?.classList.remove('opacity-50');
    document
      .querySelectorAll('.shopping-list-row-drop-target')
      .forEach((element) => element.classList.remove('shopping-list-row-drop-target'));
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLTableCellElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLTableCellElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const fromKey =
        event.dataTransfer.getData(SHOPPING_LIST_REORDER_MIME) ||
        event.dataTransfer.getData('text/plain');

      if (fromKey && productKey) {
        onReorderProducts(fromKey, productKey);
      }

      document
        .querySelectorAll('.shopping-list-row-drop-target')
        .forEach((element) => element.classList.remove('shopping-list-row-drop-target'));
    },
    [onReorderProducts, productKey]
  );

  return (
    <GridCellBase
      {...props}
      rowSelectionEnabled={false}
      disableToolTip
      className="!px-1"
      title={t('shoppingList.reorderRow')}
    >
      <span
        role="button"
        draggable
        aria-hidden="true"
        className="flex w-full cursor-move items-center justify-center text-neutral-500"
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <SvgIcon icon={shoppingListDragIndicatorIcon} />
      </span>
    </GridCellBase>
  );
};

export default ShoppingListReorderHandleCell;
